import React from "react";
import GenericTable from "@surgia/ui/containers/table/generic-table";
import Badge from "@surgia/ui/components/badge";
import TruncatedTooltip from "@surgia/ui/components/truncatedTooltip";
import { TableColumn } from "@surgia/ui/containers/table/constants";
import checkedIcon from "@surgia/ui/icons/checkbox/checked.svg";
import uncheckedIcon from "@surgia/ui/icons/checkbox/unchecked.svg";
import { formatDateWithMoment } from "@surgia/utils";

interface IPurchaseRequestTableProps {
  data: any;
  loading?: boolean;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  showBranchColumn?: boolean;
  selectedPurchaseRequests: string[];
  setSelectedPurchaseRequests: (
    requests: string[] | ((prev: string[]) => string[])
  ) => void;
  status: string | undefined;
  onViewDetails: (prId: string, branchId: string) => void;
  sortState?: { column: string | null; direction: "asc" | "desc" | null };
  onSort?: (sortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => void;
  idColumnIcon?: string;
  hasViewPermission?: boolean;
  hasEditDraftPOPermission?: boolean;
}

const PurchaseRequestTable: React.FC<IPurchaseRequestTableProps> = ({
  data,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  showBranchColumn = true,
  selectedPurchaseRequests,
  setSelectedPurchaseRequests,
  status,
  onViewDetails,
  sortState,
  onSort,
  idColumnIcon,
  hasViewPermission,
  hasEditDraftPOPermission,
}) => {
  //   const { t } = useTranslation();

  const handleCheckboxToggle = (id: string) => {
    setSelectedPurchaseRequests((prev) => {
      if (prev.includes(id)) {
        return prev.filter((requestId) => requestId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const baseColumns: TableColumn[] = [
    {
      key: "id",
      translationKey: "ID",
      sortable: true,
      sortType: "number",
      sortKey: "id",
      render: (value: string, row: any) => (
        <div className="flex items-center gap-2">
          {status == "SUBMITTED" && hasEditDraftPOPermission && (
            <img
              src={
                selectedPurchaseRequests.includes(value)
                  ? checkedIcon
                  : uncheckedIcon
              }
              alt="checked"
              onClick={() => handleCheckboxToggle(value)}
              className="cursor-pointer"
            />
          )}

          {hasViewPermission ? (
            <div className="relative group">
              <button
                onClick={() => onViewDetails(value, row.branchId)}
                className="text-purple-700 hover:text-purple-700/80 transition-colors text-[14px] cursor-pointer"
              >
                {value}
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
                {`View ${value}`}
              </div>
            </div>
          ) : (
            <div className="text-[#181D27] text-[14px] font-medium">
              {value}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "requestedBy",
      translationKey: "Requested By",
      sortable: true,
      sortType: "string",
      sortKey: "createdBy",
      render: (value) => (
        <div className="text-gray-500">
          <TruncatedTooltip content={value} useTableRowMain={true} as="p" />
        </div>
      ),
    },
    {
      key: "branch",
      translationKey: "Branch",
      sortable: true,
      sortType: "string",
      sortKey: "branch.name",
      render: (value: string) => (
        <div className="relative group">
          <Badge
            variant="default"
            className="bg-gray-100 text-gray-700 border-gray-200 truncate max-w-[150px]"
          >
            {value}
          </Badge>
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
            {value}
          </div>
        </div>
      ),
    },
    {
      key: "quantity",
      translationKey: "Qty",
      sortable: true,
      sortType: "number",
      sortKey: "totalQuantity",
    },
    {
      key: "totalPrice",
      translationKey: "Total Price (RS)",
      sortable: true,
      sortType: "number",
      sortKey: "purchaseRequestPrice.price",
    },
    {
      key: "dateCreated",
      translationKey: "Date Created",
      sortable: true,
      sortType: "date",
      sortKey: "createdAt",
      render: (value: string) => (
        <p className="table-row-sub">
          {value ? formatDateWithMoment(value) : "-"}
        </p>
      ),
    },
  ];

  // Filter out branches column if showSelectedBranchColumn is false
  const columns = baseColumns.filter(
    (column) => showBranchColumn || column.key !== "branch"
  );

  return (
    <GenericTable
      columns={columns}
      data={data}
      loading={loading}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      sortState={sortState}
      onSort={onSort}
      enableInternalSorting={false}
      idColumnIcon={idColumnIcon}
    />
  );
};

export default PurchaseRequestTable;
